import { useRef, useState } from 'react';
import { toast } from 'sonner';
import { Ban, Loader2, Paperclip, Plus, Upload } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useCancelMyExpenseMutation, useFileExpenseMutation, useMyExpensesQuery, useOpenDocument } from '@/client/api/business/spend';
import { EXPENSE_STATUS_BADGE, EXPENSE_STATUS_LABEL, type Expense } from '@/client/types/business/spend';
import { DeleteConfirmDialog, EmptyState, Panel, RowMenu, StatusBadge } from '../HumanResource/components/hr-ui';
import { money, totals } from '../HumanResource/components/comp-format';
import { formatDay } from '../HumanResource/components/leave-format';
import { Field, inputClass, today } from '../Accounting/components/spend-ui';

/** My expense claims: file one (with a receipt when storage is set up),
 *  follow it through approval and reimbursement, withdraw it before then. */
export function ExpensesTab() {
  const q = useMyExpensesQuery();
  const file = useFileExpenseMutation();
  const cancel = useCancelMyExpenseMutation();
  const openDoc = useOpenDocument('workplace');
  const fileRef = useRef<HTMLInputElement>(null);
  const [receipt, setReceipt] = useState<File | null>(null);
  const [form, setForm] = useState({ spentOn: today(), categoryId: '', description: '', amount: '', currency: 'GHS' });
  const [withdrawing, setWithdrawing] = useState<Expense | null>(null);

  if (q.isLoading) {
    return (
      <div className="flex items-center justify-center gap-2 rounded-xl border border-dashed border-gray-200 bg-white py-14 text-sm text-gray-500">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading your expenses…
      </div>
    );
  }
  if (q.isError || !q.data) return <EmptyState label={`Could not load your expenses: ${(q.error as Error)?.message ?? 'unknown error'}`} />;
  const { expenses, categories, receiptsEnabled } = q.data;
  const owed = expenses.filter((x) => x.status === 'submitted' || x.status === 'approved');

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    file.mutate(
      {
        spentOn: form.spentOn,
        categoryId: form.categoryId || undefined,
        description: form.description.trim(),
        amount: Number(form.amount),
        currency: form.currency.trim().toUpperCase(),
        file: receipt,
      },
      {
        onSuccess: () => {
          toast.success('Claim sent. Your manager has been asked to approve it.');
          setForm({ ...form, description: '', amount: '' });
          setReceipt(null);
        },
        onError: (err) => toast.error((err as Error).message || 'Could not send the claim'),
      },
    );
  };

  return (
    <div className="grid gap-4 lg:grid-cols-3">
      <Panel title="Claim an expense">
        <form onSubmit={submit} className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Date spent">
              <input type="date" required max={today()} value={form.spentOn} onChange={(e) => setForm({ ...form, spentOn: e.target.value })} className={inputClass} />
            </Field>
            <Field label="Category">
              <select value={form.categoryId} onChange={(e) => setForm({ ...form, categoryId: e.target.value })} className={inputClass}>
                <option value="">Pick one</option>
                {categories.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </Field>
          </div>
          <Field label="What for">
            <input required value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className={inputClass} placeholder="Taxi to client meeting" />
          </Field>
          <div className="grid grid-cols-3 gap-3">
            <Field label="Amount" className="col-span-2">
              <input required type="number" min={0} step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className={inputClass} />
            </Field>
            <Field label="Currency">
              <input required maxLength={3} value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value })} className={inputClass} />
            </Field>
          </div>
          {receiptsEnabled ? (
            <div>
              <input ref={fileRef} type="file" accept="application/pdf,image/*" className="hidden" onChange={(e) => setReceipt(e.target.files?.[0] ?? null)} />
              <Button type="button" variant="outline" size="sm" className="gap-1.5" onClick={() => fileRef.current?.click()}>
                <Upload className="h-3.5 w-3.5" /> {receipt ? receipt.name : 'Attach receipt (PDF or photo)'}
              </Button>
            </div>
          ) : (
            <p className="text-xs text-gray-500">Receipt uploads are not switched on yet. Keep your receipt; accounting may ask for it.</p>
          )}
          <Button type="submit" className="w-full gap-2" disabled={file.isPending}>
            {file.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Send claim
          </Button>
        </form>
      </Panel>

      <Panel title="My claims" className="lg:col-span-2" action={owed.length ? <span className="text-xs text-gray-500">Owed to you: {totals(owed)}</span> : undefined}>
        {expenses.length === 0 ? (
          <p className="py-6 text-center text-sm text-gray-500">No claims yet.</p>
        ) : (
          <ul className="divide-y divide-gray-50">
            {expenses.map((x) => (
              <li key={x.id} className="flex items-start justify-between gap-3 py-2.5">
                <div className="min-w-0">
                  <p className="text-sm text-gray-900">
                    {x.description} · {money(x.amount, x.currency)}
                  </p>
                  <p className="text-xs text-gray-500">
                    {formatDay(x.spentOn)}
                    {x.hasReceipt && ' · receipt attached'}
                    {x.reimbursedAt && ` · paid ${formatDay(x.reimbursedAt)}`}
                  </p>
                  {x.decisionNote && <p className="text-xs text-gray-500">“{x.decisionNote}”</p>}
                </div>
                <div className="flex items-center gap-1">
                  <StatusBadge label={EXPENSE_STATUS_LABEL[x.status]} className={EXPENSE_STATUS_BADGE[x.status]} />
                  <RowMenu
                    items={[
                      { label: 'Open receipt', icon: Paperclip, hidden: !x.hasReceipt, onSelect: () => openDoc(`/expenses/${x.id}/receipt`).catch((e) => toast.error((e as Error).message)) },
                      { label: 'Withdraw', icon: Ban, danger: true, hidden: x.status !== 'submitted', onSelect: () => setWithdrawing(x) },
                    ]}
                  />
                </div>
              </li>
            ))}
          </ul>
        )}
      </Panel>

      <DeleteConfirmDialog
        open={!!withdrawing}
        onOpenChange={(o) => !o && setWithdrawing(null)}
        title="Withdraw this claim?"
        description={withdrawing ? `${withdrawing.description}, ${money(withdrawing.amount, withdrawing.currency)}.` : ''}
        actionLabel="Withdraw"
        onConfirm={() => {
          if (withdrawing) cancel.mutate(withdrawing.id, { onSuccess: () => toast.success('Withdrawn'), onError: (e) => toast.error((e as Error).message) });
          setWithdrawing(null);
        }}
      />
    </div>
  );
}
